/* eslint-disable @next/next/no-img-element */
import { useLayoutEffect, useMemo, useRef, useState } from 'react';

interface ResponsiveImageProps {
    src: string;
    alt: string;
    responsiveImages?: {
        urls: string[];
        base64svg: string;
    };
    containerClassName?: string;
    imageClassName?: string;
    onLoaded?: () => void;
}

const ResponsiveImage = ({
    src,
    alt,
    responsiveImages,
    containerClassName,
    imageClassName,
    onLoaded,
}: ResponsiveImageProps) => {
    const imageRef = useRef<HTMLImageElement>(null);
    const [isLoaded, setIsLoaded] = useState<boolean>(false);

    const srcSet = useMemo<string>(() => {
        setIsLoaded(imageRef.current?.complete ?? false);
        if (!responsiveImages) return '';
        const baseUrlObject = new URL(src);
        const baseUrlString =
            baseUrlObject.origin +
            baseUrlObject.pathname.slice(
                0,
                baseUrlObject.pathname.lastIndexOf('/'),
            );
        let srcSet = '';

        responsiveImages.urls.forEach((fileName) => {
            const fileWValue = fileName.split('_').at(-2);
            srcSet += `${baseUrlString}/responsive-images/${fileName} ${fileWValue}w, `;
        });

        return `${srcSet}${responsiveImages.base64svg} 32w`;
    }, [responsiveImages, src, setIsLoaded]);

    useLayoutEffect(() => {
        if (isLoaded) {
            onLoaded?.();
        }
    }, [isLoaded, onLoaded]);

    return (
        <div className={`relative ${containerClassName}`}>
            {!!responsiveImages && (
                <img
                    src={responsiveImages?.base64svg}
                    alt=""
                    aria-hidden="true"
                    role="presentation"
                    className={`pointer-events-none absolute inset-0 ${
                        isLoaded ? 'opacity-0' : 'opacity-100'
                    } duration-1000 motion-safe:transition-all`}
                />
            )}
            <img
                ref={imageRef}
                src={src}
                alt={alt}
                sizes="50vw"
                srcSet={srcSet}
                className={`${
                    isLoaded ? 'opacity-100' : 'opacity-0'
                } duration-500 motion-safe:transition-all ${imageClassName}`}
                onLoad={() => setIsLoaded(true)}
            />
        </div>
    );
};

export default ResponsiveImage;
